import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { enqueueSnackbar } from 'notistack';
import AgentService from './agent.service';
import { agentFeatureKeys } from './agent.options';

type ActionType = 'updateAgentFeature' | 'updateBanner';

interface MutationAction {
  actionType: ActionType;
  [key: string]: any;
}

const useAgentMutation = (): {
  agentFeature: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  agentFeatureMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  isUpdatingAgentFeature: Record<string, boolean>;
  setIsUpdatingAgentFeature: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
} => {
  const [isUpdatingAgentFeature, setIsUpdatingAgentFeature] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();

  const executeMutation = async ({ actionType, ...payload }: MutationAction) => {
    const agentService = AgentService();
    console.log('payload:', payload);
    const action: Record<ActionType, (payload: any) => Promise<any>> = {
      updateAgentFeature: (params) => agentService.updateAgentFeature(params.payload, params.data),
      updateBanner: (params) => agentService.updateAgentFeature(params.payload, params.data),
    };

    const serviceMethod = action[actionType];
    if (!serviceMethod) {
      throw new Error(`Unknown action type: ${actionType}`);
    }
    return serviceMethod(payload);
  };

  const mutation = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    mutationKey: ['agentFeature-mutation'],
    onMutate: async ({ variables, actionType, payload }) => {
      setIsUpdatingAgentFeature({ ...isUpdatingAgentFeature, [actionType]: true });
      console.log('variables:', variables);
    },
    onSuccess: (data: any, variables: any, payload: any) => {
      console.log('variables:', variables);
      if (variables.actionType === 'updateAgentFeature') {
        enqueueSnackbar(`AgentFeature ${variables.payload.Flags.ServerSidePaging ? 'Server Side Paging' : 'Client Side Paging'} updated successfully`, { variant: 'success' });
      }
    },
    onError: (error: any, variables: any) => {
      console.error('Error during mutation:', error);
      if (variables.actionType === 'updateAgentFeature') {
        enqueueSnackbar('Error updating AgentFeature', { variant: 'error' });
      }
      if (variables.actionType === 'updateBanner') {
        enqueueSnackbar('Error removing the Scribe Banner', { variant: 'error' });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: agentFeatureKeys.all, exact: false });
    },
  });

  return {
    agentFeature: mutation.mutate,
    agentFeatureMutation: mutation,
    isUpdatingAgentFeature,
    setIsUpdatingAgentFeature,
  };
};

export default useAgentMutation;
